var _excluded = ["className", "defaultValue", "delay", "disabled", "helperText", "i18n", "id", "label", "name", "onChange", "onClear", "onScrollEnd", "onSuggestionSelected", "placeholder", "scrollEndOffset", "separateLabel", "suggestions", "value", "visibleOptions"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import mergeRefs from 'react-merge-refs';
import scrollEnded from '../utils/scroll_ended';
import IconSearch from '../icons/icon_search';
import OptionsList from './options_list';
import Popover from '../shared/popover';
import TextBox from '../text_box';
import useControlled from '../utils/use_controlled';
import useDebounceCallback from '../utils/use_debounce_callback';
import UtilityButton from '../shared/utility_button';
import useCheckResize from '../utils/use_check_resize';
import propTypes from './prop_types';
import searchI18n from './i18n';
import useStyles from './use_styles';
import { jsx as ___EmotionJSX } from "@emotion/core";
function addSuggestionDivider(visibleText) {
  return {
    divider: true,
    value: visibleText
  };
}
function filterSuggestions(suggestions, value, i18n) {
  var filteredSuggestions = [];
  if (value.length) {
    filteredSuggestions = suggestions.filter(suggestion => suggestion.label ? suggestion.label.toLowerCase().indexOf(value.toLowerCase()) >= 0 : suggestion.value.toLowerCase().indexOf(value.toLowerCase()) >= 0);
  }
  var {
    length
  } = filteredSuggestions;
  if (length) {
    var i18nLabel = length > 1 ? i18n.showingResults : i18n.showingResult;
    var finalLabel = i18nLabel.replace(/(%s)/g, length.toString(10));
    filteredSuggestions.unshift(addSuggestionDivider(finalLabel));
  }
  return filteredSuggestions;
}
var Search = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      className = 'vn-search',
      defaultValue,
      delay = 0,
      disabled = false,
      helperText,
      i18n: i18nProp,
      id = 'search',
      label,
      name,
      onChange,
      onClear,
      onScrollEnd,
      onSuggestionSelected,
      placeholder,
      scrollEndOffset = 0,
      separateLabel = false,
      suggestions,
      value: valueProp,
      visibleOptions = 10
    } = _ref,
    inputProps = _objectWithoutProperties(_ref, _excluded);
  var i18n = React.useMemo(() => _objectSpread(_objectSpread({}, searchI18n), i18nProp), [i18nProp]);
  var isControlledDelayed = delay && valueProp !== undefined;
  var [value, setValue] = useControlled({
    controlled: delay ? undefined : valueProp,
    default: isControlledDelayed ? valueProp : defaultValue
  });
  var filteredSuggestions = React.useMemo(() => {
    return filterSuggestions(suggestions || [], value || '', i18n);
  }, [suggestions, value, i18n]);
  var [open, setOpen] = React.useState(() => !!filteredSuggestions.length);
  var [highlightedIndex, setHighlightedIndex] = React.useState(undefined);
  var [anchorEl, setAnchorEl] = React.useState(null);
  var hasCompositionStarted = React.useRef(false);
  var highlightedRef = React.useRef(null);
  var inputRef = React.useRef();
  var optionListRef = React.useRef(null);
  var checkResize = useCheckResize();
  var handleInputRefs = mergeRefs([inputRef, ref]);
  React.useEffect(() => {
    if (!value || value === '') {
      setOpen(false);
    }
  }, [value]);
  React.useEffect(() => {
    if (isControlledDelayed) {
      setValue(valueProp);
    }
  }, [isControlledDelayed, setValue, valueProp]);
  React.useEffect(() => {
    // To scroll on keyboard navigation, if needed.
    var {
      current: highlightedSuggestion
    } = highlightedRef;
    if (highlightedSuggestion) {
      if (open) {
        highlightedSuggestion.focus();
      } else {
        highlightedSuggestion.blur();
      }
    }
  }, [highlightedIndex, highlightedRef, open]);
  React.useEffect(() => {
    var handleWindowClick = event => {
      var _inputRef$current;
      if (inputRef !== null && inputRef !== void 0 && (_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 && _inputRef$current.contains(event.target)) return;
      setOpen(false);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('click', handleWindowClick);
    }
    return () => {
      window.removeEventListener('click', handleWindowClick);
    };
  }, []);
  var debouncedCallback = useDebounceCallback(searchedValue => {
    if (onChange && !hasCompositionStarted.current) {
      onChange(searchedValue);
    }
  }, delay);
  var handleUpdate = React.useCallback(newValue => {
    if (delay > 0) {
      debouncedCallback(newValue);
    } else {
      onChange === null || onChange === void 0 || onChange(newValue);
    }
  }, [debouncedCallback, delay, onChange]);
  var handleChange = React.useCallback(newValue => {
    setValue(newValue);
    setHighlightedIndex(undefined);
    if (suggestions && suggestions.length) {
      setOpen(true);
    }
    handleUpdate(newValue);
  }, [handleUpdate, setValue, suggestions]);
  var handleComposition = React.useCallback(event => {
    switch (event.type) {
      case 'compositionupdate':
        if (event.data.length !== 0) {
          hasCompositionStarted.current = true;
          handleUpdate(value);
        }
        break;
      case 'compositionstart':
        hasCompositionStarted.current = true;
        break;
      case 'compositionend':
        if (hasCompositionStarted.current) {
          hasCompositionStarted.current = false;
        }
        handleUpdate(value);
        break;
      default:
        break;
    }
  }, [handleUpdate, value]);
  var handleClearOnClick = React.useCallback(() => {
    if (disabled) return;
    setOpen(false);
    setHighlightedIndex(undefined);
    setValue('');
    onClear === null || onClear === void 0 || onClear();
    onChange === null || onChange === void 0 || onChange('');
  }, [disabled, onChange, onClear, setValue]);
  var handleClick = React.useCallback((event, selectedOption) => {
    if (!selectedOption) return;
    var newValue = selectedOption.label || selectedOption.value;
    setOpen(false);
    setHighlightedIndex(undefined);
    setValue(newValue);
    if (onSuggestionSelected) onSuggestionSelected(selectedOption);
    onChange === null || onChange === void 0 || onChange(newValue);
    debouncedCallback.cancel();
  }, [debouncedCallback, onChange, onSuggestionSelected, setValue]);
  var handleKeyDown = React.useCallback(event => {
    var _inputRef$current2;
    var firstIndex = 0;

    // - 2 because optionList considers the label `Showing %s result` in its length:
    var lastIndex = filteredSuggestions.length - 2;
    switch (event.key) {
      case 'Tab':
        setOpen(false);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'Up': // IE Compat
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(highlightedIndex => {
          if (highlightedIndex === undefined) {
            return lastIndex;
          }
          if (highlightedIndex > firstIndex) {
            return highlightedIndex - 1;
          }
          return highlightedIndex;
        });
        break;
      case 'Down': // IE Compat
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(highlightedIndex => {
          if (highlightedIndex === undefined) {
            return firstIndex;
          }
          if (highlightedIndex < lastIndex) {
            return highlightedIndex + 1;
          }
          return highlightedIndex;
        });
        break;
      default:
        // To continue typing
        (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
        if (delay) {
          setOpen(false);
        }
        break;
    }
  }, [delay, filteredSuggestions.length]);
  var handleOptionsKeyDown = React.useCallback((event, selectedOption) => {
    if (event.key === 'Enter') {
      var _inputRef$current3;
      handleClick(event, selectedOption);
      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.focus();
    } else {
      handleKeyDown(event);
    }
  }, [handleClick, handleKeyDown]);
  var handleScroll = React.useCallback(event => {
    if (onScrollEnd && scrollEnded(event.target, scrollEndOffset)) {
      onScrollEnd();
    }
  }, [onScrollEnd, scrollEndOffset]);
  var hasValue = value && value.length > 0;
  var clearButton = hasValue && !disabled ? ___EmotionJSX(UtilityButton, {
    "aria-label": i18n.clear,
    onClick: handleClearOnClick,
    title: i18n.clear
  }) : null;
  var searchButton = ___EmotionJSX(UtilityButton, {
    icon: ___EmotionJSX(IconSearch, null),
    disabled: disabled
  });
  var hasSuggestions = filteredSuggestions.length > 0;
  var style = useStyles({
    hasSuggestions,
    open
  });
  var ariaProps = suggestions ? {
    role: 'combobox',
    'aria-haspopup': 'listbox',
    'aria-expanded': open && hasSuggestions,
    'aria-owns': "".concat(id, "-option_list")
  } : undefined;
  var anchorWidth = React.useMemo(() => {
    var _anchorEl$getBounding;
    return (anchorEl === null || anchorEl === void 0 || (_anchorEl$getBounding = anchorEl.getBoundingClientRect()) === null || _anchorEl$getBounding === void 0 ? void 0 : _anchorEl$getBounding.width) || 0;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [anchorEl, checkResize]);
  var showHelperWithSuggestions = !hasSuggestions ? helperText : '';
  return ___EmotionJSX("div", {
    className: className,
    css: style.container
  }, ___EmotionJSX("div", _extends({}, ariaProps, {
    className: "".concat(className, "-input_container"),
    css: style.inputContainer,
    ref: setAnchorEl
  }), ___EmotionJSX(TextBox, _extends({}, inputProps, {
    autoComplete: "off",
    disabled: disabled,
    id: id,
    label: label,
    name: name,
    onChange: handleChange,
    helperText: suggestions ? showHelperWithSuggestions : helperText,
    onCompositionEnd: handleComposition,
    onCompositionStart: handleComposition,
    onCompositionUpdate: handleComposition,
    onKeyDown: handleKeyDown,
    placeholder: placeholder,
    ref: handleInputRefs,
    separateLabel: separateLabel,
    trailingButton: clearButton,
    trailingIcon: searchButton,
    type: suggestions ? 'text' : 'search',
    value: value || ''
  }))), hasSuggestions && open && anchorEl && ___EmotionJSX(Popover, {
    anchorEl: anchorEl,
    css: style.popover,
    fixed: true,
    open: true,
    placement: "bottom-start",
    portal: true,
    role: "presentation"
  }, ___EmotionJSX("div", {
    css: style.optionListContainer,
    role: "presentation",
    style: {
      width: "".concat(anchorWidth, "px")
    }
  }, ___EmotionJSX(OptionsList, {
    highlightedIndex: highlightedIndex,
    highlightedRef: highlightedRef,
    id: "".concat(id, "-option_list"),
    onKeyDown: handleOptionsKeyDown,
    onClick: handleClick,
    onScroll: handleScroll,
    options: filteredSuggestions,
    ref: optionListRef,
    visibleOptions: visibleOptions
  }))));
});
Search.propTypes = propTypes;
Search.displayName = 'Search';
export default Search;